export const ACTIVATION_POLICY_OPTIONS = [
  { value: 'all', text: '모든 선행 조건 충족 (all)' },
  { value: 'any', text: '하나 이상의 선행 조건 충족 (any)' },
  { value: 'conditional', text: '조건식으로 설정 (conditional)' },
]
export const ACTIVATION_MAX_DEPTH = 8
export const ACTIVATION_MAX_LEAVES = 64
export const activationEdgeKey = edge => JSON.stringify([edge.from_node_key, edge.condition])
const copy = value => JSON.parse(JSON.stringify(value))
const isObject = value => value !== null && typeof value === 'object' && !Array.isArray(value)

export function activationIncomingOptions(clientKey, items = [], flows = []) {
  const names = new Map(items.map(item => [String(item.client_key), item.name || item.work_item?.name || item.runtime_definition?.name || item.client_key]))
  const seen = new Set()
  return flows.filter(flow => String(flow.to_node_key) === String(clientKey)).flatMap(flow => {
    const edge = { from_node_key: String(flow.from_node_key), condition: flow.condition || 'completed' }
    const value = activationEdgeKey(edge)
    if (seen.has(value)) return []
    seen.add(value)
    const condition = edge.condition === 'output_available' ? 'Runtime 출력 준비 시' : '선행 완료 시'
    return [{ value, edge, text: `${edge.from_node_key} · ${names.get(edge.from_node_key) || edge.from_node_key} · ${condition}` }]
  })
}

export function activationPolicyChange(policy, item = {}, incoming = []) {
  if (!incoming.length) policy = 'all'
  return {
    activation_policy: policy,
    activation_rule: policy === 'conditional'
      ? copy(item.activation_rule || { version: 1, op: 'all', args: incoming.map(edge => ({ from_node_key: edge.from_node_key, condition: edge.condition })) })
      : null,
  }
}

const errorMessages = {
  ACTIVATION_POLICY_NOT_SUPPORTED: '활성화 정책을 선택해 주세요.',
  ACTIVATION_ROOT_POLICY_INVALID: '선행 연결이 없는 할일은 모든 선행 조건 충족(all)만 사용할 수 있습니다. 정책을 all로 변경해 주세요.',
  ACTIVATION_RULE_INVALID: '활성화 조건식을 확인해 주세요. 각 그룹에 조건이 하나 이상 필요하며, all·any 정책에는 조건식을 사용할 수 없습니다.',
  ACTIVATION_RULE_SOURCE_MISMATCH: '연결이 변경되었거나 삭제된 선행 조건이 있습니다. 조건을 다시 선택하거나 제거해 주세요.',
  ACTIVATION_RULE_LIMIT_EXCEEDED: '조건식은 중첩 깊이 8단계, 개별 조건 64개까지 설정할 수 있습니다.',
}

export function activationPolicyError(item = {}, incoming = []) {
  const policy = item.activation_policy === undefined ? 'all' : item.activation_policy
  const rule = item.activation_rule ?? null
  let code = null
  if (!ACTIVATION_POLICY_OPTIONS.some(option => option.value === policy)) code = 'ACTIVATION_POLICY_NOT_SUPPORTED'
  else if (!incoming.length && (policy !== 'all' || rule !== null)) code = 'ACTIVATION_ROOT_POLICY_INVALID'
  else if (policy !== 'conditional') code = rule === null ? null : 'ACTIVATION_RULE_INVALID'
  else {
    const identities = new Set(incoming.map(activationEdgeKey))
    let leaves = 0
    const visit = (expression, depth = 1) => {
      if (depth > ACTIVATION_MAX_DEPTH) return 'ACTIVATION_RULE_LIMIT_EXCEEDED'
      if (!isObject(expression)) return 'ACTIVATION_RULE_INVALID'
      const keys = Object.keys(expression).sort().join(',')
      if (depth > 1 && keys === 'condition,from_node_key') {
        if (typeof expression.from_node_key !== 'string' || !expression.from_node_key || typeof expression.condition !== 'string') return 'ACTIVATION_RULE_INVALID'
        if (!identities.has(activationEdgeKey(expression))) return 'ACTIVATION_RULE_SOURCE_MISMATCH'
        leaves += 1
        return leaves > ACTIVATION_MAX_LEAVES ? 'ACTIVATION_RULE_LIMIT_EXCEEDED' : null
      }
      if (keys !== (depth === 1 ? 'args,op,version' : 'args,op')) return 'ACTIVATION_RULE_INVALID'
      if (depth === 1 && expression.version !== 1) return 'ACTIVATION_RULE_INVALID'
      if (!['all', 'any'].includes(expression.op) || !Array.isArray(expression.args) || !expression.args.length) return 'ACTIVATION_RULE_INVALID'
      for (const argument of expression.args) {
        const error = visit(argument, depth + 1)
        if (error) return error
      }
      return null
    }
    code = visit(rule)
  }
  return code ? { code, message: errorMessages[code] } : null
}

export function assertActivationPolicies(nodes = [], edges = []) {
  for (const node of nodes) {
    if (node.node_type !== 'work_item') continue
    const incoming = edges.filter(edge => edge.to_node_key === node.client_key)
    const issue = activationPolicyError(node.work_item || {}, incoming)
    if (issue) {
      const error = new Error(`할일 ${node.client_key}: ${issue.message}`)
      error.code = issue.code
      error.client_key = node.client_key
      throw error
    }
  }
}
